import { Link } from 'found';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, FormattedPlural, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import {
  Button, Card, CardBody, CardFooter, Col, Row,
} from 'reactstrap';

import Loading from 'src/components/Loading';
import {
  getTradeRulesAcceptedForProperty,
} from 'src/components/TradeRule/helpers';
import LinkInI18nBlock from 'src/enosikit/components/LinkInI18nBlock';
import { emptyTradeRulesClassName } from 'src/helpers/tradeHelpers';
import {
  RESIDUAL, TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL, TRADE_TYPE_RESIDUAL,
} from 'src/util/constants';

import ResidualTradeRule from './ResidualTradeRule';

const ResidualTradeRulesFragment = graphql`
  fragment ResidualTradeRules_property on Property {
    uuid
    meters {
      edges {
        node {
          uuid
          identifier
          title
          tradePointId
          residualRules: rules(first: 500, type: TRADE_TYPE_RESIDUAL, state: TRADE_RULE_STATE_ACCEPTED) {
            edges {
              node {
                uuid
                priority
                tradeType
                state
                buyer {
                  userId
                  communityId
                  residualId
                  tradePoint {
                    uuid
                  }
                }
                seller {
                  userId
                  communityId
                  residualId
                  tradePoint {
                    uuid
                  }
                }
                clauses {
                  edges {
                    node {
                      price
                    }
                  }
                }
                start
                finish
                proposedAt
                proposedBy {
                  uuid
                  email
                  givenName
                  familyName
                }
                acceptedAt
                acceptedBy {
                  uuid
                  email
                  givenName
                  familyName
                }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * ResidualTradeRules component displays residual trade rules for a property.
 * @param {object} props - Component props
 * @param {object} props.property - Property containing meters and their associated residual rules
 * @returns {React.ReactElement} A Card component containing residual trade information.
 */
function ResidualTradeRules(props) {
  if (!props || Object.keys(props).length === 0) {
    return <Loading />;
  }
  let { property } = props;
  const intl = useIntl();
  property = useFragment(ResidualTradeRulesFragment, property);

  const tradeRules = getTradeRulesAcceptedForProperty(property, TRADE_TYPE_RESIDUAL);
  const noTradeRulesClass = emptyTradeRulesClassName(tradeRules, RESIDUAL);

  const singleTrade = intl.formatMessage({ id: 'trade_rule.trade_rule_active.residual_trade_rules.trade.singular', defaultMessage: 'retailer default trade rule' });
  const multipleTrade = intl.formatMessage({ id: 'trade_rule.trade_rule_active.residual_trade_rules.trade.multiple', defaultMessage: 'retailer default trade rules' });
  const retailerDefaultTradesTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_active.residual_trade_rules.title', defaultMessage: 'Retailer Default Trades' });
  const buyingTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_active.residual_trade_rules.buy.title', defaultMessage: 'Buying' });
  const sellingTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_active.residual_trade_rules.sell.title', defaultMessage: 'Selling' });
  const viewAgreedTariffsLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_active.residual_trade_rules.view_agreed_tariffs.label', defaultMessage: 'View agreed energy tariffs' });

  return (
    <Card className={`mt-4 mb-4 trade-rules-residual${noTradeRulesClass}`}>
      <CardBody>
        <h2>{retailerDefaultTradesTitle}</h2>
        <p>
          <FormattedMessage
            id="trade_rule.trade_rule_active.residual_trade_rules.help_text"
            defaultMessage="The retailer default trades are at the agreed energy tariffs that you have contracted with your retailer."
            values={{
              link: (chunks) => (
                <LinkInI18nBlock
                  url={intl.formatMessage({ id: 'trade_rule.trade_rule_active.residual_trade_rules.help_text.url', defaultMessage: 'trade_rule.trade_rule_active.residual_trade_rules.help_text.url' })}
                  urlKey="trade_rule.trade_rule_active.residual_trade_rules.help_text.url"
                  chunks={chunks}
                />
              ),
            }}
          />
        </p>
        <Row>
          <Col xs="12" sm="12" md="6">
            <h3>{buyingTitle}</h3>
            <p>
              {intl.formatMessage(
                { id: 'trade_rule.trade_rule_active.residual_trade_rules.buy.count_details', defaultMessage: 'You have {tradeRulesCount} {tradeRulesCountLabel} to buy energy.' },
                {
                  tradeRulesCount: <strong>{tradeRules.buy.length}</strong>,
                  tradeRulesCountLabel: <FormattedPlural
                    value={tradeRules.buy.length}
                    one={singleTrade}
                    other={multipleTrade}
                  />,
                },
              )}
            </p>
            {tradeRules?.buy?.map((rule) => (
              <ResidualTradeRule
                rule={rule}
                property={property}
                tradeDirection={TRADE_DIRECTION_BUY}
                key={rule.uuid}
              />
            ))}
          </Col>
          <Col xs="12" sm="12" md="6">
            <h3>{sellingTitle}</h3>
            <p>
              {intl.formatMessage(
                { id: 'trade_rule.trade_rule_active.residual_trade_rules.sell.count_details', defaultMessage: 'You have {tradeRulesCount} {tradeRulesCountLabel} to sell energy.' },
                {
                  tradeRulesCount: <strong>{tradeRules.sell.length}</strong>,
                  tradeRulesCountLabel: <FormattedPlural
                    value={tradeRules.sell.length}
                    one={singleTrade}
                    other={multipleTrade}
                  />,
                },
              )}
            </p>
            {tradeRules?.sell?.map((rule) => (
              <ResidualTradeRule
                rule={rule}
                property={property}
                tradeDirection={TRADE_DIRECTION_SELL}
                key={rule.uuid}
              />
            ))}
          </Col>
        </Row>
      </CardBody>
      <CardFooter>
        <Link to={`/properties/${property.uuid}/settings#tariffs`}>
          <Button color="primary" className="me-2">
            {viewAgreedTariffsLabel}
          </Button>
        </Link>
      </CardFooter>
    </Card>
  );
}

ResidualTradeRules.propTypes = {
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

ResidualTradeRules.defaultProps = {
  property: null,
};

export default ResidualTradeRules;
